import { supabase } from "@/services/supabaseClient";

const VITALREP_SOURCE = "vitalrep";
const NOT_CAPTURED = "not_captured";

function cleanText(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function toInteger(value) {
  const text = cleanText(value).replace(/[^\d.-]+/g, "");
  if (!text) return null;
  const number = Number.parseInt(text, 10);
  return Number.isFinite(number) ? number : null;
}

function toNumber(value, decimals = 2) {
  const text = cleanText(value).replace(",", ".").replace(/[^\d.-]+/g, "");
  if (!text) return null;
  const number = Number.parseFloat(text);
  if (!Number.isFinite(number)) return null;
  return Number(number.toFixed(decimals));
}

export function parseBloodPressure(value) {
  const text = cleanText(value);
  if (!text) return { systolic: null, diastolic: null };

  const match = text.match(/(\d{2,3})\s*(?:\/|over)\s*(\d{2,3})/i);
  if (!match) return { systolic: null, diastolic: null };

  const systolic = Number.parseInt(match[1], 10);
  const diastolic = Number.parseInt(match[2], 10);
  return {
    systolic: Number.isFinite(systolic) ? systolic : null,
    diastolic: Number.isFinite(diastolic) ? diastolic : null,
  };
}

export function normalizeVitalRepVitalsPayload(vitals = {}) {
  const source = cleanText(vitals?.source).toLowerCase() || NOT_CAPTURED;
  if (source === NOT_CAPTURED) return null;

  const { systolic, diastolic } = parseBloodPressure(
    vitals?.bloodPressure ?? vitals?.blood_pressure,
  );
  const payload = {
    systolic_bp: systolic,
    diastolic_bp: diastolic,
    heart_rate: toInteger(vitals?.heartRate ?? vitals?.heart_rate),
    temperature_celsius: toNumber(vitals?.temperature),
    oxygen_saturation: toInteger(vitals?.oxygenSaturation ?? vitals?.oxygen_saturation),
    respiratory_rate: toInteger(vitals?.respiratoryRate ?? vitals?.respiratory_rate),
    weight_kg: toNumber(vitals?.weight),
    height_cm: toInteger(vitals?.heightCm ?? vitals?.height_cm ?? vitals?.height),
  };

  const hasAnyValue = Object.values(payload).some((value) => value !== null);
  return hasAnyValue ? payload : null;
}

export function formatCanonicalVitals(row = {}) {
  if (!row || typeof row !== "object") return [];
  const bloodPressure =
    row.systolic_bp && row.diastolic_bp
      ? `${row.systolic_bp}/${row.diastolic_bp} mmHg`
      : "";

  return [
    ["bloodPressure", bloodPressure],
    ["heartRate", row.heart_rate ? `${row.heart_rate} bpm` : ""],
    ["temperature", row.temperature_celsius ? `${row.temperature_celsius} °C` : ""],
    ["oxygenSaturation", row.oxygen_saturation ? `${row.oxygen_saturation}%` : ""],
    ["respiratoryRate", row.respiratory_rate ? `${row.respiratory_rate}/min` : ""],
    ["weight", row.weight_kg ? `${row.weight_kg} kg` : ""],
    ["height", row.height_cm ? `${row.height_cm} cm` : ""],
  ].filter(([, value]) => cleanText(value));
}

export async function syncVitalRepVitalSigns({
  patientId,
  doctorId,
  appointmentId,
  consultationReportId,
  vitals,
  capturedAt,
  notes = "",
} = {}) {
  const normalized = normalizeVitalRepVitalsPayload(vitals);
  if (!normalized) return { data: null, skipped: true, reason: "empty_vitals" };

  const cleanPatientId = cleanText(patientId);
  const cleanDoctorId = cleanText(doctorId);
  const cleanAppointmentId = cleanText(appointmentId);
  const cleanConsultationReportId = cleanText(consultationReportId);
  if (!cleanPatientId || !cleanDoctorId || !cleanAppointmentId || !cleanConsultationReportId) {
    return { data: null, skipped: true, reason: "missing_lineage" };
  }

  const timestamp = cleanText(capturedAt) || new Date().toISOString();
  const { data, error } = await supabase.rpc("sync_vitalrep_vital_signs", {
    p_patient_id: cleanPatientId,
    p_doctor_id: cleanDoctorId,
    p_appointment_id: cleanAppointmentId,
    p_consultation_report_id: cleanConsultationReportId,
    p_captured_at: timestamp,
    p_recorded_at: timestamp,
    p_systolic_bp: normalized.systolic_bp,
    p_diastolic_bp: normalized.diastolic_bp,
    p_heart_rate: normalized.heart_rate,
    p_temperature_celsius: normalized.temperature_celsius,
    p_weight_kg: normalized.weight_kg,
    p_height_cm: normalized.height_cm,
    p_oxygen_saturation: normalized.oxygen_saturation,
    p_respiratory_rate: normalized.respiratory_rate,
    p_notes: notes,
  });

  if (error) throw error;
  return { data, skipped: false, source: VITALREP_SOURCE };
}

export async function fetchLatestPatientVitalSigns(patientId) {
  const cleanPatientId = cleanText(patientId);
  if (!cleanPatientId) return null;

  const { data, error } = await supabase.rpc("get_my_latest_vital_signs");

  if (error && error.code !== "PGRST116") throw error;
  return data || null;
}

export async function fetchLatestPermittedPatientVitalSigns({ patientId, appointmentId, accessStatus } = {}) {
  if (cleanText(accessStatus).toLowerCase() !== "granted") return null;
  const cleanPatientId = cleanText(patientId);
  const cleanAppointmentId = cleanText(appointmentId);
  if (!cleanPatientId || !cleanAppointmentId) return null;

  const { data, error } = await supabase.rpc("get_permitted_latest_vital_signs", {
    p_patient_id: cleanPatientId,
    p_appointment_id: cleanAppointmentId,
  });

  if (error && error.code !== "PGRST116") throw error;
  return data || null;
}
